/** Shared car-image bucket + URL helpers (client + server). */

export const CAR_IMAGES_BUCKET = "car-images";
export const CAR_UPLOAD_PREFIX = "/uploads/cars/";

const SUPABASE_CAR_PUBLIC_RE =
  /\/storage\/v1\/object\/public\/car-images\/(.+)$/i;

export function supabasePublicCarImageUrl(
  supabaseUrl: string,
  storagePath: string,
): string {
  const base = supabaseUrl.replace(/\/$/, "");
  const encoded = storagePath
    .split("/")
    .map((part) => encodeURIComponent(part))
    .join("/");
  return `${base}/storage/v1/object/public/${CAR_IMAGES_BUCKET}/${encoded}`;
}

/** Extract object path inside `car-images` (e.g. `abc.jpg` or `users/logo.png`). */
export function fileNameFromCarImageHref(href: string): string | null {
  const trimmed = href.trim();
  if (!trimmed) return null;

  const objectMatch = trimmed.match(SUPABASE_CAR_PUBLIC_RE);
  if (objectMatch?.[1]) return decodeURIComponent(objectMatch[1]);

  if (trimmed.startsWith(CAR_UPLOAD_PREFIX)) {
    return trimmed.slice(CAR_UPLOAD_PREFIX.length);
  }
  if (trimmed.startsWith("uploads/cars/")) {
    return trimmed.slice("uploads/cars/".length);
  }
  if (!trimmed.includes("/") && /\.(jpe?g|png|webp|gif)$/i.test(trimmed)) {
    return trimmed;
  }
  return null;
}

export function isLocalCarUploadPath(href: string | null | undefined): boolean {
  if (!href?.trim()) return false;
  const t = href.trim();
  return t.startsWith(CAR_UPLOAD_PREFIX) || t.startsWith("uploads/cars/");
}

/** Prefer Supabase public URL when we know the storage object key. */
export function normalizeCarImageHref(href: string, supabaseUrl: string): string {
  const trimmed = href.trim();
  if (!trimmed) return trimmed;

  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
    const key = fileNameFromCarImageHref(trimmed);
    if (key && trimmed.includes("/storage/v1/object/public/")) {
      return supabasePublicCarImageUrl(supabaseUrl, key);
    }
    return trimmed;
  }

  const key = fileNameFromCarImageHref(trimmed);
  if (key) {
    return supabasePublicCarImageUrl(supabaseUrl, key);
  }

  return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
}

export function normalizeCarImageList(
  hrefs: string[] | undefined,
  supabaseUrl: string,
): string[] | undefined {
  if (!hrefs?.length) return hrefs;
  return hrefs.map((h) => normalizeCarImageHref(h, supabaseUrl));
}
